import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, Mail, Briefcase, MapPin, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

const Account = () => {
  const { user } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message: string }

  const roleLabel = (() => {
    const r = (user?.role || '').toLowerCase();
    if (r === 'energy_manager' || r === 'admin') return 'Administrateur / Energy Manager';
    if (r === 'responsable_agence') return "Responsable d'Agence";
    if (r === 'commercial') return 'Commercial';
    return user?.role || '—';
  })();

  const submit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    if (newPassword !== confirmPassword) {
      setFeedback({ type: 'error', message: 'Les deux nouveaux mots de passe ne correspondent pas.' });
      return;
    }
    if (newPassword.length < 6) {
      setFeedback({ type: 'error', message: 'Le mot de passe doit contenir au moins 6 caractères.' });
      return;
    }
    if (newPassword === currentPassword) {
      setFeedback({ type: 'error', message: 'Le nouveau mot de passe doit être différent de l\'actuel.' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setFeedback({ type: 'error', message: data?.detail || 'Erreur lors du changement de mot de passe.' });
      } else {
        setFeedback({ type: 'success', message: data?.message || 'Mot de passe modifié avec succès.' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      setFeedback({ type: 'error', message: 'Erreur réseau, veuillez réessayer.' });
    } finally {
      setLoading(false);
    }
  };

  const PwField = ({ label, value, onChange, show, setShow, testid }) => (
    <div>
      <label className="block text-sm font-medium text-[#191D1E] mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-2.5 pr-10 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] focus:border-transparent text-sm"
          required
          minLength={6}
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#687175] hover:text-[#191D1E]"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="account-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-heading font-semibold text-[#191D1E]">Paramètres du compte</h1>
        <p className="text-sm text-[#687175] mt-1">Gérez vos informations personnelles et votre mot de passe.</p>
      </div>

      {user?.must_change_password && (
        <div className="mb-6 p-4 bg-orange-50 border-2 border-orange-300 rounded-xl flex items-start gap-3" data-testid="account-must-change-banner">
          <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-orange-900">Changement de mot de passe obligatoire</h3>
            <p className="text-sm text-orange-800 mt-1">
              Votre administrateur a réinitialisé votre mot de passe. Veuillez le remplacer ci-dessous par un mot de passe personnel pour accéder au reste de l'application.
            </p>
          </div>
        </div>
      )}

      {/* Profil */}
      <section className="bg-white border border-[#BFC6CC] rounded-xl p-6 mb-6">
        <h2 className="text-base font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-[#397DFF]" /> Mon profil
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <InfoRow icon={User} label="Nom complet" value={user?.name || `${user?.prenom || ''} ${user?.nom || ''}`.trim() || '—'} />
          <InfoRow icon={Mail} label="Email" value={user?.email || '—'} />
          <InfoRow icon={Briefcase} label="Rôle" value={roleLabel} />
          <InfoRow icon={MapPin} label="Agence" value={user?.agence || user?.agence_nom || '—'} />
        </div>
      </section>

      {/* Changement de mot de passe */}
      <section className="bg-white border border-[#BFC6CC] rounded-xl p-6">
        <h2 className="text-base font-semibold text-[#191D1E] mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#397DFF]" /> Changer mon mot de passe
        </h2>

        <form onSubmit={submit} className="space-y-4">
          <PwField
            label="Mot de passe actuel"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            setShow={setShowCurrent}
            testid="account-current-password"
          />
          <PwField
            label="Nouveau mot de passe (minimum 6 caractères)"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            setShow={setShowNew}
            testid="account-new-password"
          />
          <PwField
            label="Confirmer le nouveau mot de passe"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showConfirm}
            setShow={setShowConfirm}
            testid="account-confirm-password"
          />

          {feedback && (
            <div
              className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm ${
                feedback.type === 'success'
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
              data-testid={feedback.type === 'success' ? 'account-success' : 'account-error'}
            >
              {feedback.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              )}
              <span>{feedback.message}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            className="bg-[#397DFF] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#2E5BCC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="account-submit-password"
          >
            {loading ? 'Modification…' : 'Modifier le mot de passe'}
          </button>
        </form>
      </section>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 bg-[#FAFBFC] rounded-lg border border-[#F5F6F9]">
    <Icon className="w-4 h-4 text-[#687175] mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-xs text-[#687175] uppercase tracking-wider">{label}</div>
      <div className="text-sm font-medium text-[#191D1E] truncate">{value}</div>
    </div>
  </div>
);

export default Account;
